import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography, useTheme } from '@mui/material';
import { useHistory } from 'react-router-dom';

import { DataTable, useResolutionCheck } from 'web-components';

import T from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ROUTE_BATCH_RECORDS } from '../../attrs/routePaths';
import MobileView from './MobileView';
import Footer from '../DataTable/Footer';
import { generateColumns } from './BatchRecordsOverview.config';
import { StyledContainer } from './BatchRecordsOverview.styled';
import { PaginationResponse } from '../../helpers/propTypes';
import { getBatchesFiltered } from '../../redux/batch/actions';
import { PAGINATION_DEFAULT_ROWS_PER_PAGE, PAGINATION_DEFAULT_START_PAGE } from '../../attrs/pagination';
import { getBatchSortOrder } from '../../redux/batch/selectors';
import DesktopView from './DesktopView';
import { BATCH_DEFAULT_LIST_ORDER, BATCH_DEFAULT_LIST_SORT } from '../../attrs/batch';
import RunningBatchAccordion from './RunningBatchAccordion';
import { BatchListContext } from './BatchListContext';
import { StyledMarginDiv, StyledPaper } from './RunningBatchAccordion.styled';
import { useDebouncedValue } from '../../hooks';

const BatchRecordsOverview = ({ rows }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const batchSortOrder = useSelector(getBatchSortOrder);

  const [isLoading, setIsLoading] = useState(false);
  const [currentView, setCurrentView] = useState([]);
  const [currentSort, setCurrentSort] = useState({
    colId: batchSortOrder.sort || BATCH_DEFAULT_LIST_SORT,
    order: batchSortOrder.order || BATCH_DEFAULT_LIST_ORDER
  });
  const shouldSkipInitialLoad = useRef(true);
  const { isMobile } = useResolutionCheck();
  const { t } = useTranslation();
  const history = useHistory();
  const rowsPerPage = PAGINATION_DEFAULT_ROWS_PER_PAGE;

  const [batchId, setBatchId] = useState('');
  const [machine, setMachine] = useState('');
  const [recipe, setRecipe] = useState('');
  const debouncedBatchId = useDebouncedValue(batchId);
  const debouncedMachine = useDebouncedValue(machine);
  const debouncedRecipe = useDebouncedValue(recipe);

  useEffect(() => {
    setIsLoading(false);
    if (rows && rows.data && rows.data.length) {
      setCurrentView(DataTable.sortFn(rows.data, currentSort.colId, currentSort.order));
    } else {
      setCurrentView([]);
    }
  }, [currentSort, rows]);

  const handleChangePage = page => {
    setIsLoading(true);
    dispatch(getBatchesFiltered(page, rowsPerPage, currentSort.colId, currentSort.order, batchId, machine, recipe));
  };

  const handleSort = (colId, order) => {
    setIsLoading(true);
    setCurrentSort({ colId, order });
  };

  const handleSearchBatchID = value => {
    setBatchId(value);
  };

  const handleSearchMachine = value => {
    setMachine(value);
  };

  const handleSearchRecipe = value => {
    setRecipe(value);
  };

  const loadData = useCallback(() => {
    dispatch(
      getBatchesFiltered(
        PAGINATION_DEFAULT_START_PAGE,
        rowsPerPage,
        currentSort.colId,
        currentSort.order,
        debouncedBatchId,
        debouncedMachine,
        debouncedRecipe
      )
    );
  }, [debouncedBatchId, debouncedMachine, debouncedRecipe, dispatch, rowsPerPage, currentSort]);

  useEffect(() => {
    if (shouldSkipInitialLoad.current) {
      shouldSkipInitialLoad.current = false;
      return;
    }

    setIsLoading(true);
    loadData();
  }, [loadData]);

  const handleSelectRow = row => history.push(`/${ROUTE_BATCH_RECORDS}/${row.id}`);

  const props = {
    dataSelectorTable: 'batch-records-table',
    dataSelectorRows: 'batch-records-table-rows',
    isLoading,
    columns: generateColumns(t),
    rows: currentView,
    footer: (
      <Footer
        currentPage={rows.page}
        totalPages={rows.total_pages || 1}
        totalItems={rowsPerPage || 0}
        onChange={handleChangePage}
      />
    ),
    defaultSort: currentSort,
    sortFn: handleSort,
    onClick: handleSelectRow
  };

  const search = {
    handleSearchBatchID,
    handleSearchMachine,
    handleSearchRecipe,
    batchId,
    machine,
    recipe
  };

  const clearAllFields = () => {
    setBatchId('');
    setMachine('');
    setRecipe('');
  };

  const reloadBatchList = () => {
    // This logic to reload is used that way because when you clear the filters it also reloads the list
    // so this avoids reloading more than once
    if (!batchId && !machine && !recipe) {
      loadData();
    } else {
      setBatchId('');
      setMachine('');
      setRecipe('');
    }
  };

  return (
    <BatchListContext.Provider value={reloadBatchList}>
      <StyledContainer maxWidth={false} theme={theme}>
        <RunningBatchAccordion />
        <StyledMarginDiv marginTop="20px">
          <StyledPaper elevation={3} sx={{ pl: 5 }}>
            <Stack spacing={2} sx={{ paddingBottom: '1rem' }}>
              <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>{t('batch_records.batch_list')}</Typography>
            </Stack>
            {isMobile ? (
              <MobileView {...props} search={search} clearAllFields={clearAllFields} />
            ) : (
              <DesktopView props={props} search={search} clearAllFields={clearAllFields} />
            )}
          </StyledPaper>
        </StyledMarginDiv>
      </StyledContainer>
    </BatchListContext.Provider>
  );
};

BatchRecordsOverview.propTypes = {
  rows: T.shape(PaginationResponse).isRequired
};

export default BatchRecordsOverview;
